import { Shuffle, EyeOff, AlertTriangle, Calendar } from "lucide-react";
import { Section, GridContainer } from "../layout";
import { AnimatedSection } from "../shared";
import { useGridIntensityZone } from "../../hooks";

/**
 * Problem section explaining pain points that Hulahoop solves.
 * Uses id="problema" for smooth scroll targeting from Hero.
 */
export function Problem() {
  const sectionRef = useGridIntensityZone<HTMLElement>({ intensity: "visible" });

  return (
    <Section
      ref={sectionRef}
      id="problema"
      size="md"
      noPaddingBottom
      className="relative z-10 pb-32 md:pb-16 xl:pb-20"
    >
      <AnimatedSection>
        <GridContainer>
          {/* Header */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12 text-center">
            <span className="text-accent text-sm font-medium tracking-wide uppercase">
              THE PROBLEM
            </span>
            <h2 className="mt-4 text-2xl md:text-3xl xl:text-4xl font-semibold text-text-primary tracking-tight">
              El crecimiento no debería aumentar el caos.
            </h2>
            <p className="mt-6 text-body text-text-secondary">
              Hoy, gestionar incentivos fiscales culturales implica:
            </p>
          </div>

          {/* Pain Points Grid - Double dashed border box */}
          <div className="col-span-4 md:col-span-8 xl:col-span-12 mt-8 relative overflow-hidden">
            {/* Top double lines */}
            <div className="absolute -left-6 -right-6 md:-left-16 md:-right-16 xl:-left-[180px] xl:-right-[180px] top-0 flex flex-col gap-2 md:gap-3">
              <div
                className="border-t border-dashed"
                style={{ borderColor: "rgba(228, 228, 231, 0.5)" }}
              />
              <div
                className="border-t border-dashed"
                style={{ borderColor: "rgba(228, 228, 231, 0.5)" }}
              />
            </div>

            {/* Bottom double lines */}
            <div className="absolute -left-6 -right-6 md:-left-16 md:-right-16 xl:-left-[180px] xl:-right-[180px] bottom-0 flex flex-col gap-2 md:gap-3">
              <div
                className="border-t border-dashed"
                style={{ borderColor: "rgba(228, 228, 231, 0.5)" }}
              />
              <div
                className="border-t border-dashed"
                style={{ borderColor: "rgba(228, 228, 231, 0.5)" }}
              />
            </div>

            {/* Left double lines - extend beyond box */}
            <div className="absolute left-0 -top-4 -bottom-4 flex flex-row gap-2 md:gap-3">
              <div
                className="border-l border-dashed h-full"
                style={{ borderColor: "rgba(228, 228, 231, 0.5)" }}
              />
              <div
                className="border-l border-dashed h-full"
                style={{ borderColor: "rgba(228, 228, 231, 0.5)" }}
              />
            </div>

            {/* Right double lines - extend beyond box */}
            <div className="absolute right-0 -top-4 -bottom-4 flex flex-row gap-2 md:gap-3">
              <div
                className="border-l border-dashed h-full"
                style={{ borderColor: "rgba(228, 228, 231, 0.5)" }}
              />
              <div
                className="border-l border-dashed h-full"
                style={{ borderColor: "rgba(228, 228, 231, 0.5)" }}
              />
            </div>

            {/* Content */}
            <div className="py-6 px-4 xs:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 justify-items-center items-start gap-x-2 xs:gap-x-4 gap-y-6">
                <div className="flex flex-col items-center text-center">
                  <div className="h-5 flex items-center justify-center">
                    <Shuffle size={16} className="text-text-tertiary" />
                  </div>
                  <p className="text-xs xs:text-sm font-medium text-text-tertiary text-center mt-1">
                    <span className="hidden xs:inline">7 herramientas dispersas</span>
                    <span className="xs:hidden">7 herramientas</span>
                  </p>
                  <p className="text-xs text-text-secondary mt-1 max-w-[180px] xs:max-w-none">
                    Excel, email, Drive, WhatsApp. Nada conectado.
                  </p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="h-5 flex items-center justify-center">
                    <EyeOff size={16} className="text-text-tertiary" />
                  </div>
                  <p className="text-xs xs:text-sm font-medium text-text-tertiary text-center mt-1">
                    <span className="hidden xs:inline">Cero visibilidad</span>
                    <span className="xs:hidden">Sin visibilidad</span>
                  </p>
                  <p className="text-xs text-text-secondary mt-1 max-w-[180px] xs:max-w-none">
                    Todo en silos. Visión fragmentada.
                  </p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="h-5 flex items-center justify-center">
                    <AlertTriangle size={16} className="text-text-tertiary" />
                  </div>
                  <p className="text-xs xs:text-sm font-medium text-text-tertiary text-center mt-1">
                    <span className="hidden xs:inline">Riesgo en cada paso</span>
                    <span className="xs:hidden">Riesgo constante</span>
                  </p>
                  <p className="text-xs text-text-secondary mt-1 max-w-[180px] xs:max-w-none">
                    Error pequeño, consecuencias grandes.
                  </p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="h-5 flex items-center justify-center">
                    <Calendar size={16} className="text-text-tertiary" />
                  </div>
                  <p className="text-xs xs:text-sm font-medium text-text-tertiary text-center mt-1">
                    <span className="hidden xs:inline">31 dic. Sin margen.</span>
                    <span className="xs:hidden">31 dic.</span>
                  </p>
                  <p className="text-xs text-text-secondary mt-1 max-w-[180px] xs:max-w-none">
                    Deadline fijo. Todo a la vez.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </GridContainer>
      </AnimatedSection>
    </Section>
  );
}
